import { useTranslation } from 'react-i18next';
import { TableRow } from '@mui/material';
import { formatNumber } from 'shared/utils';
import { Currencies } from 'shared/types';
import {
  FooterDivider,
  FooterLabelCol,
  FooterTableCell,
  StyledFooter,
} from './styles';
import { renderLabelCell } from './renderers/labelCellRenderer';
import BudgetTypography from './components/BudgetTypography/BudgetTypography';
import { renderBudgetCell } from './renderers/budgetCellRenderer';
import { FooterData } from '../../types';

const FOOTER_HEIGHT = 10;
const CELL_HEIGHT = FOOTER_HEIGHT / 4;

type Key = keyof FooterData;
interface RowColorMap {
  [key: number]: string;
  default: string;
}
interface TableFooterProps {
  data: FooterData;
  currency: Currencies;
  exchangeRate?: number;
  freeColumns?: number;
}
const rowColorMap: RowColorMap = {
  0: 'lorealPurple.main',
  1: 'lorealBlue.main',
  2: 'black',
  default: 'textDark.light',
};

const paddingMap = {
  0: '1rem 0rem 1rem 1rem',
  default: '0rem 1rem 0rem 1rem',
};

const budgetPaddingMap = {
  0: '1rem 24px 0 0',
  default: '0 24px 0 0',
};

function TableFooter({
  data,
  currency,
  exchangeRate,
  freeColumns = 2,
}: TableFooterProps) {
  const { t } = useTranslation();
  return (
    <StyledFooter sx={{ height: `${FOOTER_HEIGHT}rem` }}>
      <TableRow>
        <FooterDivider colSpan={100} />
      </TableRow>
      {Object.entries(data).map(([key, value], index) => {
        const isLastRow = index === Object.keys(data).length - 1;
        const isFirstRow = index === 0;

        const rowColor = rowColorMap[index] || rowColorMap.default;
        const dividerHeight = `${
          CELL_HEIGHT + (isLastRow || isFirstRow ? 1 : 0)
        }rem`;
        const labelCellStyles = {
          height: `${CELL_HEIGHT}rem`,
          padding: isLastRow
            ? '0rem 1rem 1rem 1rem'
            : paddingMap[isFirstRow ? 0 : 'default'],
        };
        const budgetCellStyles = {
          height: `${CELL_HEIGHT}rem`,
          padding: budgetPaddingMap[isFirstRow ? 0 : 'default'],
        };

        const { budget, ...metrics } = value;

        return (
          <TableRow key={key}>
            <FooterLabelCol colSpan={freeColumns} align="left" />
            {renderLabelCell(
              t(`mediaPlanning.ooh.${key}`) as Key,
              rowColor,
              labelCellStyles,
              t,
              exchangeRate
            )}
            {Object.entries(metrics).map(([metricKey, metric]) => {
              if (
                (!isFirstRow &&
                  (metricKey === 'weeks' || metricKey === 'grp')) ||
                metricKey === 'split'
              ) {
                return (
                  <FooterTableCell
                    sx={budgetCellStyles}
                    key={metricKey}
                    align={metricKey === 'weeks' ? 'right' : 'center'}
                  >
                    -
                  </FooterTableCell>
                );
              }
              if (metricKey === 'weeks' || metricKey === 'grp') {
                return (
                  <FooterTableCell
                    key={metricKey}
                    sx={budgetCellStyles}
                    align="center"
                  >
                    <BudgetTypography
                      color={rowColor}
                      value={formatNumber(metric)}
                      align={metricKey === 'weeks' ? 'right' : 'center'}
                    />
                  </FooterTableCell>
                );
              }
              return null;
            })}
            <FooterTableCell sx={budgetCellStyles} align="right">
              {renderBudgetCell(
                { value: budget ?? 0, currency: 'PLN' },
                rowColor,
                currency,
                exchangeRate
              )}
            </FooterTableCell>
          </TableRow>
        );
      })}
    </StyledFooter>
  );
}

export default TableFooter;
